/* Duallys Only — Flashy Hero
   Mobile performance update:
   - keeps above-the-fold category tile images from competing with below-the-fold hero slides
   - loads mobile-optimized hero backgrounds only when the hero is close to the viewport
*/
(() => {
  const initFlashyHero = () => {
  const hero = document.querySelector('[data-do-flashy-hero]');
  if (!hero) return;

  document.body.classList.add('do-home-has-hero');

  const isMobile = () => window.matchMedia('(max-width: 749px)').matches;
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const setScrolled = () => {
    const scrolled = window.scrollY > 10;
    document.body.classList.toggle('do-scrolled', scrolled);
  };
  setScrolled();
  window.addEventListener('scroll', setScrolled, { passive: true });

  // Keep reveal effects away from above-the-fold mobile content.
  // Vehicle picker and category tiles should paint immediately for PageSpeed/LCP.
  const revealTargets = [
    '.dually-brand-logo-grid__item',
    '.featured-collection .card-wrapper'
  ];
  const nodes = revealTargets
    .flatMap((sel) => Array.from(document.querySelectorAll(sel)))
    .filter(Boolean);

  if (!prefersReducedMotion && !isMobile()) {
    nodes.forEach((n) => n.classList.add('do-reveal'));

    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('is-visible');
            io.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -10% 0px', threshold: 0.1 });

      nodes.forEach((n) => io.observe(n));
    } else {
      nodes.forEach((n) => n.classList.add('is-visible'));
    }
  }

  const slides = Array.from(hero.querySelectorAll('[data-do-hero-slide]'));
  const prevButton = hero.querySelector('[data-do-hero-prev]');
  const nextButton = hero.querySelector('[data-do-hero-next]');

  const getSlideBackground = (slide) => {
    if (!slide) return '';
    if (isMobile() && slide.dataset.doBgMobile) return slide.dataset.doBgMobile;
    return slide.dataset.doBg || '';
  };

  const loadResponsiveMedia = (slide) => {
    if (!slide || !isMobile()) return;
    const mobileSources = slide.querySelectorAll('source[data-do-mobile-srcset]');
    mobileSources.forEach((source) => {
      const mobileSrcset = source.getAttribute('data-do-mobile-srcset');
      if (!mobileSrcset) return;
      source.setAttribute('srcset', mobileSrcset);
      source.removeAttribute('data-do-mobile-srcset');
    });
  };

  const ensureSlideBackground = (slide) => {
    if (!slide || slide.querySelector('.do-flashy-hero__media')) return;
    if (!getSlideBackground(slide)) return;
    const selectedBackground = getSlideBackground(slide);
    if (slide.dataset.doBgLoaded === selectedBackground) return;
    slide.style.setProperty('--do-bg-loaded', `url("${selectedBackground}")`);
    slide.dataset.doBgLoaded = selectedBackground;
  };

  if (!slides.length) return;

  const preloadAdjacentBackgrounds = (activeIndex) => {
    loadResponsiveMedia(slides[activeIndex]);
    ensureSlideBackground(slides[activeIndex]);
    if (!isMobile()) {
      loadResponsiveMedia(slides[(activeIndex + 1) % slides.length]);
      ensureSlideBackground(slides[(activeIndex + 1) % slides.length]);
    }
  };

  let idx = 0;
  let timer = null;
  let initialized = false;

  const show = (next) => {
    idx = (next + slides.length) % slides.length;
    preloadAdjacentBackgrounds(idx);
    slides.forEach((el, i) => el.classList.toggle('is-active', i === idx));
    if (prevButton) prevButton.setAttribute('aria-label', `Previous slide (currently ${idx + 1} of ${slides.length})`);
    if (nextButton) nextButton.setAttribute('aria-label', `Next slide (currently ${idx + 1} of ${slides.length})`);
  };

  const stop = () => {
    if (timer) window.clearInterval(timer);
    timer = null;
  };

  const start = () => {
    stop();
    if (slides.length > 1 && !prefersReducedMotion) {
      const beginRotation = () => { timer = window.setInterval(() => show(idx + 1), 13000); };
      if (isMobile() && document.readyState !== 'complete') {
        window.addEventListener('load', beginRotation, { once: true });
      } else {
        beginRotation();
      }
    }
  };

  const initializeHero = () => {
    if (initialized) return;
    initialized = true;
    show(0);
    start();
  };

  if (prevButton) {
    prevButton.addEventListener('click', (event) => {
      event.stopPropagation();
      initializeHero();
      show(idx - 1);
      start();
    });
  }

  if (nextButton) {
    nextButton.addEventListener('click', (event) => {
      event.stopPropagation();
      initializeHero();
      show(idx + 1);
      start();
    });
  }

  slides.forEach((slide) => {
    const link = slide.dataset.slideLink;
    if (!link) return;

    slide.addEventListener('click', (event) => {
      if (event.target.closest('[data-do-hero-prev], [data-do-hero-next]')) return;
      window.location.href = link;
    });
  });

  document.addEventListener('visibilitychange', () => {
    if (document.hidden) stop();
    else if (initialized) start();
  });

  const heroNearViewport = () => {
    const rect = hero.getBoundingClientRect();
    return rect.top < window.innerHeight + 100;
  };

  if (isMobile() && !heroNearViewport() && 'IntersectionObserver' in window) {
    const heroObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          initializeHero();
          heroObserver.disconnect();
        }
      });
    }, { rootMargin: '100px 0px', threshold: 0.01 });

    heroObserver.observe(hero);
  } else {
    initializeHero();
  }
  };

  if (window.matchMedia('(max-width: 749px)').matches && document.readyState !== 'complete') {
    window.addEventListener('load', function () {
      var runHero = function () { initFlashyHero(); };
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(runHero, { timeout: 1200 });
      } else {
        window.setTimeout(runHero, 300);
      }
    }, { once: true });
  } else {
    initFlashyHero();
  }
})();
